import { ArrowRight, FileText, FolderTree, Image as ImageIcon, Plus, ScrollText, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { getRepo, isDemoMode, type Article, type AuditEntry, type Category, type MediaItem, type TeamMember } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { relativeTime } from '../../lib/format';
import { Badge, Card, CardBody, EmptyState, Spinner } from '../../components/ui';

interface DashboardData {
  articles: Article[];
  categories: Category[];
  media: MediaItem[];
  team: TeamMember[];
  audit: AuditEntry[];
}

function StatCard({ label, value, icon, tone }: { label: string; value: number | string; icon: ReactNode; tone?: string }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted">{label}</p>
          <p className="mt-2 font-display text-3xl font-semibold">{value}</p>
        </div>
        <span className={tone ? `rounded-md p-2 ${tone}` : 'rounded-md bg-background p-2 text-muted'}>{icon}</span>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  const { data, loading, error } = useAsync<DashboardData>(async () => {
    const repo = getRepo();
    const [articles, categories, media, team, audit] = await Promise.all([
      repo.listAllArticles({ status: 'all' }),
      repo.listCategories(),
      repo.listMedia(),
      repo.listTeam(),
      repo.listAuditLog(),
    ]);
    return { articles, categories, media, team, audit };
  }, []);

  if (loading) return <Spinner label="Loading dashboard…" />;
  if (error) return <p className="text-danger" role="alert">{error}</p>;
  if (!data) return null;

  const { articles, categories, media, team, audit } = data;
  const count = (s: Article['status']) => articles.filter((a) => a.status === s).length;
  const needsReview = articles.filter((a) => a.status === 'in_review');
  const recentAudit = audit.slice(0, 5);

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold">Dashboard</h1>
          <p className="mt-1 text-sm text-muted">An overview of the publication.</p>
        </div>
        <Link
          to="/admin/articles/new"
          className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:brightness-95"
        >
          <Plus className="h-4 w-4" /> New article
        </Link>
      </header>

      {isDemoMode ? (
        <p className="rounded-lg border border-border bg-card p-3 text-xs text-muted">
          Demo mode — data is seeded in memory and resets on reload. Configure Supabase env vars for the real backend.
        </p>
      ) : null}

      <section aria-label="Key figures" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label="Total articles" value={articles.length} icon={<FileText className="h-4 w-4" />} />
        <StatCard label="Published" value={count('published')} icon={<FileText className="h-4 w-4" />} tone="bg-success/10 text-success" />
        <StatCard label="In review" value={count('in_review')} icon={<FileText className="h-4 w-4" />} tone="bg-warning/10 text-warning" />
        <StatCard label="Scheduled" value={count('scheduled')} icon={<FileText className="h-4 w-4" />} tone="bg-primary/10 text-primary" />
        <StatCard label="Media files" value={media.length} icon={<ImageIcon className="h-4 w-4" />} />
        <StatCard label="Team members" value={team.length} icon={<Users className="h-4 w-4" />} />
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <div className="flex items-center justify-between border-b border-border p-5">
            <div>
              <h2 className="font-display text-lg font-semibold">Needs attention</h2>
              <p className="mt-1 text-sm text-muted">Articles waiting on editorial review.</p>
            </div>
            <Badge tone="in_review">{needsReview.length}</Badge>
          </div>
          <CardBody className="p-0">
            {needsReview.length === 0 ? (
              <div className="p-5">
                <EmptyState title="Nothing to review" description="Articles submitted for review will appear here." />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {needsReview.map((a) => (
                  <li key={a.id}>
                    <Link to={`/admin/articles/${a.id}`} className="flex items-center justify-between gap-4 p-4 hover:bg-background">
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{a.title}</span>
                        <span className="block truncate text-xs text-muted">
                          {a.author?.name ?? 'Unassigned'} · updated {relativeTime(a.updatedAt)}
                        </span>
                      </span>
                      <Badge tone={a.status}>in review</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <div className="space-y-6">
          <Card>
            <div className="border-b border-border p-5">
              <h2 className="font-display text-lg font-semibold">Quick actions</h2>
            </div>
            <ul className="divide-y divide-border text-sm">
              <li><Link to="/admin/articles" className="flex items-center justify-between p-4 hover:bg-background">All articles <ArrowRight className="h-4 w-4 text-muted" /></Link></li>
              <li><Link to="/admin/categories" className="flex items-center justify-between p-4 hover:bg-background"><span className="inline-flex items-center gap-2"><FolderTree className="h-4 w-4 text-muted" />Categories</span><ArrowRight className="h-4 w-4 text-muted" /></Link></li>
              <li><Link to="/admin/media" className="flex items-center justify-between p-4 hover:bg-background"><span className="inline-flex items-center gap-2"><ImageIcon className="h-4 w-4 text-muted" />Media library</span><ArrowRight className="h-4 w-4 text-muted" /></Link></li>
              <li><Link to="/admin/audit" className="flex items-center justify-between p-4 hover:bg-background"><span className="inline-flex items-center gap-2"><ScrollText className="h-4 w-4 text-muted" />Audit log</span><ArrowRight className="h-4 w-4 text-muted" /></Link></li>
            </ul>
          </Card>

          <Card>
            <div className="border-b border-border p-5">
              <h2 className="font-display text-lg font-semibold">Recent activity</h2>
            </div>
            <CardBody className="p-0">
              {recentAudit.length === 0 ? (
                <p className="p-5 text-sm text-muted">No activity recorded yet.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {recentAudit.map((e) => (
                    <li key={e.id} className="p-4">
                      <p className="font-medium">{e.action}</p>
                      <p className="mt-0.5 truncate text-xs text-muted">{e.resource} · {e.actor} · {relativeTime(e.at)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <p className="text-xs text-muted">{categories.length} categories configured.</p>
    </div>
  );
}
